/**
 * alert-channel-rpc-client
 *
 * Alert channels (continuum.alertchannel) and the alerter plugins that send them, read
 * and written through the firewall (dbaccess.v1). A channel's kind names a plugin; its
 * settings (the target column) are the values of that plugin's configuration fields.
 *
 * An alerter plugin is a checkpoint function marked @plugin. The push of its fileset
 * registers it: the fileset's `plugins` setting lists the plugin and its configuration
 * fields in declared order, which is what a channel's form is built from. A field of a
 * secret type holds the NAME of a stored secret of that type, never a value.
 */

import { parseTypeTree, type TypeTree } from "../components/forms/type-tree";
import { adminRpc } from "./admin-rpc-client";

/** One field of a plugin's configuration type. `type` is as the plugin writes it: Str, Num, Bool, [Str], SlackWebhook. */
export interface PluginField {
  name: string;
  type: string;
  hasDefault: boolean;
}

/** A registered alerter plugin: the kind a channel can be. */
export interface AlerterPlugin {
  name: string;
  /** the fileset that declares it */
  fileset: string;
  /** its uid: the checkpoint vm loads the fileset's plan by it */
  filesetUid: string;
  fields: PluginField[];
  /** the configuration type as a tree, which the form is built from; absent on a registration from before the tree */
  config?: TypeTree;
}

export interface AlertChannel {
  uid: string;
  name: string;
  kind: string;
  enabled: boolean;
  /** the plugin's settings, by field name */
  target: Record<string, unknown>;
}

function asObject(v: unknown): Record<string, unknown> {
  if (typeof v === "string") {
    try {
      return asObject(JSON.parse(v));
    } catch {
      return {};
    }
  }
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

/** The alerter plugins registered by filesets, by name. */
export async function listAlerterPlugins(): Promise<AlerterPlugin[]> {
  const r = await adminRpc.query(`SELECT uid, name, extra_data FROM fileset WHERE status = 'active' ORDER BY name`);
  const plugins: AlerterPlugin[] = [];
  for (const row of r.rows) {
    const registered = asObject(row["extra_data"])["plugins"];
    if (!Array.isArray(registered)) continue;
    for (const raw of registered) {
      const p = asObject(raw);
      if (p["kind"] !== "alerter") continue;
      const fields = Array.isArray(p["fields"]) ? p["fields"] : [];
      const config = parseTypeTree(p["config"]);
      plugins.push({
        name: String(p["name"] ?? ""),
        fileset: String(row["name"] ?? ""),
        filesetUid: String(row["uid"] ?? ""),
        ...(config ? { config } : {}),
        fields: fields.map((f) => {
          const field = asObject(f);
          return {
            name: String(field["name"] ?? ""),
            type: String(field["type"] ?? ""),
            hasDefault: Boolean(field["hasDefault"]),
          };
        }),
      });
    }
  }
  return plugins.sort((a, b) => a.name.localeCompare(b.name));
}

/** Every alert channel, enabled or not, by name. */
export async function listChannels(): Promise<AlertChannel[]> {
  const r = await adminRpc.query(
    `SELECT uid, name, kind, enabled, target FROM alertchannel WHERE status = 'active' ORDER BY name`,
  );
  return r.rows.map((row) => ({
    uid: String(row["uid"] ?? ""),
    name: String(row["name"] ?? ""),
    kind: String(row["kind"] ?? ""),
    enabled: Boolean(row["enabled"]),
    target: asObject(row["target"]),
  }));
}

/** Write a channel: an insert when `isNew`, otherwise an update of its name, settings and enabled flag. */
export async function saveChannel(channel: AlertChannel, isNew: boolean): Promise<{ success: boolean; error: string }> {
  const values = { name: channel.name, target: JSON.stringify(channel.target), enabled: channel.enabled };
  try {
    const resp = isNew
      ? await adminRpc.insert("alertchannel", { uid: channel.uid, kind: channel.kind, ...values })
      : await adminRpc.update("alertchannel", { uid: channel.uid }, values);
    return { success: resp.success, error: resp.error?.message ?? "" };
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : String(e) };
  }
}
